
import { format } from "date-fns";
import { useVotingHistory } from "@/hooks/polls/useVotingHistory";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart2 } from "lucide-react";
import { useMemo, useState } from "react";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { usePagination } from "@/hooks/polls/usePagination";

export const PaginatedVotingHistoryTable = ({ userId }: { userId?: string }) => {
  const { data: votingHistory, isLoading } = useVotingHistory(userId);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  
  const { 
    currentPage, 
    setCurrentPage, 
    totalPages, 
    paginatedItems 
  } = usePagination({
    totalItems: votingHistory?.length || 0,
    initialPage: 1,
    itemsPerPage
  });
  
  const paginatedVotingHistory = useMemo(() => {
    return paginatedItems(votingHistory || []);
  }, [paginatedItems, votingHistory]);

  if (isLoading) {
    return <div>Loading voting history...</div>;
  }

  if (!votingHistory?.length) {
    return <div>No voting history found.</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart2 className="h-5 w-5" />
          Voting History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Poll</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Insight Card</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedVotingHistory.map((entry) => (
              <TableRow key={`${entry.poll_id}-${entry.voted_at}`}>
                <TableCell>{entry.poll_title}</TableCell>
                <TableCell>{format(new Date(entry.voted_at), 'PPpp')}</TableCell>
                <TableCell>
                  {entry.constituency_code ? `${entry.constituency_code}, ` : ''}
                  {entry.country_code || 'Global'}
                </TableCell>
                <TableCell>
                  {entry.insight_card_id ? (
                    <Link 
                      to={`/insights/${entry.insight_card_id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {entry.insight_card_title}
                    </Link>
                  ) : (
                    'No linked card'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {/* Only show pagination if we have more than one page */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }).map((_, index) => (
                  <PaginationItem key={index}>
                    <PaginationLink
                      isActive={currentPage === index + 1}
                      onClick={() => setCurrentPage(index + 1)}
                      className="cursor-pointer"
                    >
                      {index + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
